import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ManagementContainerModel,
  ManagementDocumentModel,
  ManagementShipmentModel,
  ManagementVoyageModel,
} from '@data-layer/models/management';
import { enumVoyageType } from '@data-layer/enums/lcl';
import { map, take, takeUntil } from 'rxjs/operators';
import { ContainerDocumentsComponent } from '@shared/lcl-mutual/voyages/components';
import { AppService } from '@app/app.service';
import { Observable, ReplaySubject } from 'rxjs';
import { enumDocumentRootEntityType, enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { PaginatedResultModel } from '@data-layer/models/common';
import { DocumentsService } from '@services/documents.service';
import { precisionRound } from '@helpers/data-transform.helper';

@Component({
  selector: 'voyage-container-results',
  templateUrl: './voyage-container-results.component.html',
  styleUrls: ['./voyage-container-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoyageContainerResultsComponent implements OnChanges, OnDestroy {
  @Input() public containers: ManagementContainerModel[] = [];
  @Input() public voyage: ManagementVoyageModel;
  @Input() public voyageType: number;
  @Output() public deleteContainerSource: EventEmitter<number> = new EventEmitter<number>();
  @Output() public moveShipmentSource: EventEmitter<{ shipmentId: number; referenceNumber: string }> = new EventEmitter<{
    shipmentId: number;
    referenceNumber: string;
  }>();
  @Output() public editContainerNumberSource: EventEmitter<ManagementContainerModel> = new EventEmitter<ManagementContainerModel>();

  public isEditContainerNumberModalOpen: boolean = false;
  public currentContainerToEdit: ManagementContainerModel = null;
  public containerNumberValue: string;
  public VOYAGE_TYPES: typeof enumVoyageType = enumVoyageType;
  public docs: { [id: number]: ManagementDocumentModel[] };
  public availableDocTypes: enumDocumentType[] = [
    enumDocumentType.DRAFT_MASTER_BILL_OF_LADING,
    enumDocumentType.SHIPPING_ORDER,
    enumDocumentType.DEBIT_NOTE,
    enumDocumentType.LOADING_PLAN,
    enumDocumentType.MANIFEST,
    enumDocumentType.MASTER_BILL_OF_LADING,
    enumDocumentType.TELEX_RELEASE,
    enumDocumentType.FREIGHT_OTHER,
  ];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private appService: AppService, private documentsService: DocumentsService, private cdr: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.containers?.firstChange && changes?.containers?.currentValue) {
      if (!!this.containers) {
        this.containers.forEach((item: ManagementContainerModel) => {
          this.docs = { ...this.docs, [item.id]: [] };
          this.getDocs(item.id);
        });
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public removeContainer(id: number): void {
    this.deleteContainerSource.emit(id);
  }

  public moveShipment(shipmentMoveToData: { shipmentId: number; referenceNumber: string }): void {
    this.moveShipmentSource.emit(shipmentMoveToData);
  }

  public changeEditContainerNumberModalState(container: ManagementContainerModel = null): void {
    this.isEditContainerNumberModalOpen = !this.isEditContainerNumberModalOpen;
    this.currentContainerToEdit = container;
    this.containerNumberValue = container?.containerNumber;
  }

  public updateContainer(): void {
    this.currentContainerToEdit.containerNumber = this.containerNumberValue;
    this.editContainerNumberSource.emit(this.currentContainerToEdit);
    this.changeEditContainerNumberModalState();
  }

  public openFilesSidebar(containerId: number): void {
    const componentSidebarRef: ComponentRef<ContainerDocumentsComponent> = this.appService.openSidebar(ContainerDocumentsComponent, {
      sidebarHeaderText: 'LABELS.ATTACH_DOCUMENT_TO_THIS_CONTAINER',
    });

    componentSidebarRef.instance.containerId = containerId;
    componentSidebarRef.instance.dataUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getDocs(containerId);
    });
    componentSidebarRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.appService.closeSidebar();
      this.getDocs(containerId);
    });
  }

  public getContainerDocs(containerId: number): ManagementDocumentModel[] {
    return this.docs?.[containerId] || [];
  }

  public getTotalCostContainer(container: ManagementContainerModel): number {
    return precisionRound(
      (container.shipments || []).reduce(
        (acc: number, currShipment: ManagementShipmentModel) => acc + currShipment.aggregatedStatistics.totalCost,
        0,
      ),
    );
  }

  private getDocs(containerId: number): void {
    this.loadDocuments(containerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((docs: ManagementDocumentModel[]) => {
        this.docs[containerId] = docs.filter((item: ManagementDocumentModel) => item.state === enumDocumentState.UPLOADED);

        this.cdr.markForCheck();
      });
  }

  private loadDocuments(containerId: number): Observable<ManagementDocumentModel[]> {
    return this.documentsService
      .getManagementDocuments({ rootEntityId: containerId, rootEntityType: enumDocumentRootEntityType.CONTAINER }, false)
      .pipe(
        map((data: PaginatedResultModel<ManagementDocumentModel>) => data.items),
        takeUntil(this.destroy$),
      );
  }
}
